import React, {useEffect} from "react";
import {IInstalledExtension} from "../../models/Extension";
import {DefaultButton, getTheme, Icon, IconButton, IColumn, SearchBox, SelectionMode, ShimmeredDetailsList, Stack, Text, Toggle, TooltipHost} from "@fluentui/react";
import Header from "../Common/Header";
import Segment from "../Common/Segment";
import {ColumnGenerator} from "../../logic/Columns/ColumnGenerator";
import InstalledExtensionsTableValidationButton from "./InstalledExtensionTableValidationButton";
import SubOrganizationBadge from "../SubOrganization/SubOrganizationBadge";
import useSubOrganizationScope from "../../hooks/useSubOrganizationScope";


const theme = getTheme();

/**
 * Friendly names for the extension types this table shows. Unknown values fall through as-is rather than
 * being hidden: a type nobody has labelled yet is still worth seeing, and a blank cell is not.
 */
const EXTENSION_TYPE_LABELS: Record<string, string> = {
    Folio: 'Folio',
    import: 'Import',
    realtime: 'Real time',
    procurement: 'Ordering',
    export: 'Export',
    directImport: 'Direct import',
    customerImport: 'Customer import',
    contactImport: 'Contact import',
    purchaseHistory: 'Purchase history',
    recommendation: 'Recommendation',
    webhook: 'Webhook',
    tax: 'Tax',
    locations: 'Locations',
    analysis: 'Analysis',
    punchout: 'Punchout',
    storefrontOpportunity: 'Storefront opportunity',
    storefrontCustomer: 'Storefront customer',
};

/**
 * What the platform currently believes about an integration's credentials.
 *
 * The same three states, and the same wording, the onboarding checklist and the distributor picker use —
 * this is one more place that answers "is it working", and a fourth vocabulary for one fact is how they
 * end up disagreeing in front of a customer.
 *
 * Never-checked is shown rather than left blank on purpose: silence reads as fine, and an integration
 * nobody has ever verified is exactly the one worth noticing. A connector that offers no way to check at
 * all says so instead of implying a verdict.
 */
/** The same red the folio panel's "settings missing" badges use — one fact, one color. */
const MISSING_SETTINGS_COLOR = '#a4262c';

const ValidationStatus: React.FC<{extension: IInstalledExtension}> = ({extension}) => {
    // Ahead of every credential verdict, including a passing one: a check can only cover the settings it
    // was given, so "verified" beside an unfinished form is true of the credentials and misleading about
    // the integration. It is also the more actionable thing to say — there is a form to go and fill in,
    // rather than a remote system to go and ask about.
    const missing = extension.missingRequiredSettings ?? 0;
    if (missing > 0) {
        return (
            <TooltipHost content={missing === 1
                ? 'One required setting has no value yet. Open the details to fill it in.'
                : `${missing} required settings have no value yet. Open the details to fill them in.`}>
                <span style={{color: MISSING_SETTINGS_COLOR, display: 'inline-flex', alignItems: 'center', gap: 4}}>
                    <Icon iconName='Warning' style={{lineHeight: 1}} />
                    <Text variant='small'>{missing === 1 ? '1 setting needed' : `${missing} settings needed`}</Text>
                </span>
            </TooltipHost>
        );
    }

    if (!extension.hasValidation) {
        return (
            <TooltipHost content='This integration does not offer a way to check credentials automatically.'>
                <Text variant='small' style={{color: theme.palette.neutralTertiary}}>Cannot be verified</Text>
            </TooltipHost>
        );
    }

    if (extension.lastValidationSuccess === true) {
        return (
            <span style={{color: theme.palette.green, display: 'inline-flex', alignItems: 'center', gap: 4}}>
                <Icon iconName='SkypeCircleCheck' style={{lineHeight: 1}} />
                <Text variant='small'>Verified</Text>
            </span>
        );
    }

    if (extension.lastValidationSuccess === false) {
        return (
            // Named as a failed CHECK, and the connector's own words quoted as such. Their wording often
            // reads like a complaint about empty fields ("username and password are required") when the
            // values are present and simply wrong — beside our own "N settings needed" state, an unlabelled
            // message is easy to read as this grid saying the form is unfinished.
            <TooltipHost content={extension.lastValidationMessage
                ? `The last credential check failed. ${extension.lastValidationMessage}`
                : 'The last credential check failed.'}>
                <span style={{color: theme.palette.redDark, display: 'inline-flex', alignItems: 'center', gap: 4}}>
                    <Icon iconName='StatusErrorFull' style={{lineHeight: 1}} />
                    <Text variant='small'>Needs attention</Text>
                </span>
            </TooltipHost>
        );
    }

    return (
        <TooltipHost content='Nothing has checked these credentials yet. Press Validate to find out.'>
            <span style={{color: theme.palette.neutralSecondary, display: 'inline-flex', alignItems: 'center', gap: 4}}>
                <Icon iconName='CircleRing' style={{lineHeight: 1, fontSize: 12}} />
                <Text variant='small'>Not checked</Text>
            </span>
        </TooltipHost>
    );
};

interface IInstalledExtensionsTableProps {
    onDetailsSelect: (extension: IInstalledExtension) => void;
    onAvailableOpen: () => void;
    onDisableToggle: (extension: IInstalledExtension) => void;
    onDelete: (extension: IInstalledExtension) => void;
    installedExtensions: IInstalledExtension[];
    isLoading: boolean;
}

const InstalledExtensionsTable: React.FC<IInstalledExtensionsTableProps> = (props) => {
    // Names for sub-org scoped installs. The list itself is already scoped server side by the
    // x-suborganization header; this only resolves the id on the row to something readable.
    const subOrganizationScope = useSubOrganizationScope();
    const [installedExtensions, setInstalledExtensions] = React.useState<IInstalledExtension[] | undefined>(props.installedExtensions);
    const [loading, setLoading] = React.useState<boolean>(props.isLoading);
    useEffect(() => {
        if(props.installedExtensions) {
            setInstalledExtensions(props.installedExtensions)
        }
    }, [props.installedExtensions]);
    useEffect(() => {
        setLoading(props.isLoading)
    }, [props.isLoading])

    const [search, setSearch] = React.useState('');
    // Both kinds on by default: the list's job is to show what is installed, and a filter that hides
    // half of it before anyone asks is a way to lose track of an integration.
    const [showFolios, setShowFolios] = React.useState(true);
    const [showExtensions, setShowExtensions] = React.useState(true);
    const [hideInactive, setHideInactive] = React.useState(false);

    const visible = (installedExtensions ?? []).filter(extension => {
        const isFolio = extension.extensionType === 'Folio';
        if (isFolio ? !showFolios : !showExtensions) return false;
        // Folio rows are always reported enabled; this is really about standalone extensions.
        if (hideInactive && extension.enabled === false) return false;

        const term = search.trim().toLowerCase();
        if (!term) return true;
        // Source is searchable too, which is the point of showing it: it is how a dev record is told
        // from the production one when the names are identical.
        return [extension.nickname, extension.name, extension.source]
            .some(value => value?.toLowerCase().includes(term));
    });

    const filtersHideEverything = visible.length === 0 && (installedExtensions?.length ?? 0) > 0;

    const filterBar = (
        <Stack
            horizontal
            wrap
            verticalAlign='center'
            tokens={{childrenGap: '1em'}}
            // A wrapping Stack gives its INNER element `margin: -0.5 * childrenGap` (Stack.styles.js) to
            // cancel the gap at the edges, so its content sits half a gap above the Stack's own top and
            // an ancestor's overflow clips whatever pokes out — here, the SearchBox's 1px resting border.
            // It looked fine when focused only because the thicker focus border reached back into view.
            // The padding gives that half-gap back. It has to match the gap; a couple of pixels does not
            // touch it.
            styles={{root: {marginBottom: '0.75em', paddingTop: '0.5em'}}}
        >
            <SearchBox
                placeholder='Search name or source'
                value={search}
                onChange={(_e, value) => setSearch(value ?? '')}
                onClear={() => setSearch('')}
                styles={{root: {width: 280}}}
            />
            <Toggle inlineLabel label='Folios' checked={showFolios} onChange={(_e, c) => setShowFolios(!!c)}
                styles={{root: {marginBottom: 0}}} />
            <Toggle inlineLabel label='Extensions' checked={showExtensions} onChange={(_e, c) => setShowExtensions(!!c)}
                styles={{root: {marginBottom: 0}}} />
            <Toggle inlineLabel label='Hide inactive' checked={hideInactive} onChange={(_e, c) => setHideInactive(!!c)}
                styles={{root: {marginBottom: 0}}} />
        </Stack>
    );
    
    if (installedExtensions?.length === 0 && !loading) {
        return (
            <div className="main-content">
                <Segment>
                    <Header size={"h3"} icon={'Puzzle'} showIconCircle={false}>
                        <Header key={'no-installed-extensions-header'} size={"h1"}>
                            It looks like you don't have any extensions installed.
                        </Header>
                        <DefaultButton primary text='Install' onClick={() => props.onAvailableOpen()} /><br /><br />
                        Click install to browse available extensions.
                    </Header>
                </Segment>
            </div>
        );
    }

    const columns: IColumn[] = [
        // Leftmost pencil, matching the house pattern for opening a row's details.
        {
            key: 'details',
            name: '',
            minWidth: 40,
            maxWidth: 40,
            onRender: (item: IInstalledExtension) => (
                <TooltipHost content='Open settings'>
                    <IconButton
                        iconProps={{iconName: 'Edit'}}
                        ariaLabel={`Open ${item.nickname ?? item.name} settings`}
                        onClick={() => props.onDetailsSelect(item)}
                    />
                </TooltipHost>
            )
        },
        {
            key: 'image',
            name: '',
            minWidth: 32,
            maxWidth: 32,
            onRender: (item: IInstalledExtension) => item.image
                ? <img
                    src={item.image}
                    alt=''
                    // Contained, not stretched: connector logos are all different shapes and a fixed box
                    // would squash half of them.
                    style={{height: 24, maxWidth: 28, objectFit: 'contain', verticalAlign: 'middle'}}
                />
                // A connector with no logo gets the same neutral glyph the picker falls back to, so rows
                // stay aligned rather than the column collapsing on some of them.
                : <Icon iconName='Trackers' style={{fontSize: 16, color: theme.palette.neutralTertiary}} />
        },
        ColumnGenerator('Name', 'name', 200, undefined, (ext) => {
            // A folio is created with its nickname set to its name, so the old "{nickname} - {name}"
            // printed the same thing twice. The second half only earns its place when the operator has
            // actually renamed the connection, and "No nickname" was never worth a column inch.
            const nickname = ext.nickname?.trim();
            const renamed = !!nickname && !!ext.name && nickname !== ext.name;
            return (
                <span>
                    {nickname || ext.name}
                    {renamed && <Text variant='small' style={{color: theme.palette.neutralSecondary}}>{` - ${ext.name}`}</Text>}
                    {ext.subOrganization && <span style={{marginLeft: '0.5em'}}>
                        <SubOrganizationBadge
                            name={subOrganizationScope.nameOf(ext.subOrganization)}
                            tooltip={`Installed for ${subOrganizationScope.nameOf(ext.subOrganization)} only, not for the whole organization.`}
                        />
                    </span>}
                </span>
            );
        }),
        ColumnGenerator('Type', 'extensionType', 120, 150, (ext) => {
            const isFolio = ext.extensionType === 'Folio';
            return (
                <Text
                    variant='small'
                    style={{
                        // A folio is a different kind of thing from the rest, not another category
                        // alongside them, so it reads a shade stronger.
                        fontWeight: isFolio ? 600 : 400,
                        color: isFolio ? theme.palette.neutralPrimary : theme.palette.neutralSecondary,
                    }}
                >
                    {EXTENSION_TYPE_LABELS[ext.extensionType ?? ''] ?? ext.extensionType ?? ''}
                </Text>
            );
        }),
        // The catalog record this was installed from. Worth its own column because the id is the only
        // thing that tells a production connector from a dev one -- the names are identical.
        ColumnGenerator('Source', 'source', 160, 220, (ext) => (
            <Text variant='small' style={{fontFamily: 'Consolas, Menlo, monospace', color: theme.palette.neutralSecondary}}>
                {ext.source}
            </Text>
        )),
        {
            key: 'status',
            name: 'Status',
            minWidth: 130,
            maxWidth: 150,
            onRender: (item: IInstalledExtension) => <ValidationStatus extension={item} />
        },
        {
            key: 'validate',
            name: '',
            minWidth: 40,
            maxWidth: 40,
            onRender: (item: IInstalledExtension) => {
                if (!item.hasValidation) return null;
                if(item.extensionType == 'Folio') return <InstalledExtensionsTableValidationButton folio={item.source} />
                return <InstalledExtensionsTableValidationButton extension={item} />
            }
        },
        ColumnGenerator('Enabled', 'enabled', 70, 70, (extension) => {
            if(extension.extensionType == 'Folio') return <></>;
            return(<Toggle
                checked={!!extension.enabled}
                ariaLabel={`${extension.enabled ? 'Disable' : 'Enable'} ${extension.nickname ?? extension.name}`}
                onChange={() => props.onDisableToggle(extension)}
                styles={{root: {marginBottom: 0}}}
            />)
        }),
        ColumnGenerator('', 'delete', 40, 40, (extension) => {
            if(extension.extensionType == 'Folio') return <></>;
            return(<TooltipHost content='Delete'>
                <IconButton
                    iconProps={{iconName: 'Delete'}}
                    ariaLabel={`Delete ${extension.nickname ?? extension.name}`}
                    onClick={() => props.onDelete(extension)}
                />
            </TooltipHost>)
        }),
    ];

    return <>
        {filterBar}
        {/* Distinct from the "nothing installed" state above: things ARE installed, the filters just
            exclude them all, and saying so stops it reading as data loss. */}
        {filtersHideEverything
            ? <Text style={{color: theme.palette.neutralSecondary}}>
                Nothing matches these filters. {(installedExtensions?.length ?? 0)} installed in total.
            </Text>
            : <ShimmeredDetailsList items={visible} enableShimmer={loading} columns={columns} selectionMode={SelectionMode.none} />}
    </>
};

export default InstalledExtensionsTable;
